import { useEffect, useRef, useState } from "react";

import type {
  DailyQueueCloseoutCommandResult,
  DailyQueueCloseoutPreview,
  DailyQueueCloseoutSnapshot,
  ParentOverrideSnapshot,
  ParentOverrideStateSnapshot,
  QueueItemCloseoutDisposition,
  Role,
} from "@vsta/contracts";

import type { FocusBlockConnectivity } from "./focus-block.js";

export type DayCloseoutUiCommand = {
  queueId: string;
  expectedRevision: number;
  expectedSchoolworkRevision: number;
  expectedWorkStateRevision: number;
  dispositions: Array<{
    queueItemId: string;
    disposition: QueueItemCloseoutDisposition;
  }>;
  overrideId?: string;
};

const stepStatusLabels = {
  "not-started": "Not Started",
  active: "Active",
  paused: "Paused",
  blocked: "Blocked",
  done: "Done",
  deferred: "Deferred",
  skipped: "Skipped",
} as const;

const assignmentStatusLabels = {
  unplanned: "Unplanned",
  planned: "Planned",
  "in-progress": "In Progress",
  "ready-to-verify": "Ready to Verify",
  completed: "Completed",
  deferred: "Deferred",
  cancelled: "Cancelled",
} as const;

function formatMinutes(durationMs: number) {
  const minutes = Math.floor(durationMs / 60_000);
  return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

function formatLocalDate(localDate: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  }).format(new Date(`${localDate}T00:00:00.000Z`));
}

function nextLocalDate(localDate: string) {
  const [year, month, day] = localDate.split("-").map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day! + 1));
  return date.toISOString().slice(0, 10);
}

function dispositionLabel(disposition: QueueItemCloseoutDisposition | null) {
  if (disposition === null) return "Finished in this Daily Queue";
  if (disposition.kind === "replan") {
    return `Replanned for ${formatLocalDate(disposition.futureLocalDate)}, position ${disposition.futurePosition}`;
  }
  if (disposition.kind === "defer") return "Deferred for later planning";
  if (disposition.kind === "retain") return "Retained for parent review";
  return "Cancelled by parent";
}

export function DayCloseoutHistory({
  closeout,
}: {
  closeout: DailyQueueCloseoutSnapshot;
}) {
  const nextStep = closeout.nextPlannedStep;
  return (
    <section
      className="day-closeout-history"
      aria-labelledby="closed-day-title"
    >
      <p className="status-label">Factual daily review</p>
      <h2 id="closed-day-title">Daily Queue closed</h2>
      <p>
        Closed by {closeout.closedBy.role === "parent" ? "Parent" : "Student"}{" "}
        on{" "}
        <time dateTime={closeout.closedAt}>
          {new Date(closeout.closedAt).toLocaleString("en-US", {
            timeZone: closeout.timezone,
          })}
        </time>
        . This preserved history does not change later.
      </p>
      <ol className="closeout-item-list">
        {closeout.items.map((item) => (
          <li key={item.queueItemId}>
            <h3>{item.stepInstruction}</h3>
            <p>
              {item.courseTitle} · {item.assignmentTitle}
            </p>
            <dl>
              <dt>Step status</dt>
              <dd>{stepStatusLabels[item.statusAtClose.step]}</dd>
              <dt>Assignment status</dt>
              <dd>{assignmentStatusLabels[item.statusAtClose.assignment]}</dd>
              <dt>End-of-day decision</dt>
              <dd>{dispositionLabel(item.disposition)}</dd>
            </dl>
          </li>
        ))}
      </ol>
      <section aria-labelledby="closeout-time-title">
        <h3 id="closeout-time-title">Separate time records</h3>
        <dl className="closeout-time-summary">
          <dt>Focus Block timer-running time</dt>
          <dd>
            {formatMinutes(
              closeout.timeSummary.focusBlockTimerRunningDurationMs,
            )}
          </dd>
          <dt>Break time</dt>
          <dd>{formatMinutes(closeout.timeSummary.breakDurationMs)}</dd>
          <dt>Transition time</dt>
          <dd>{formatMinutes(closeout.timeSummary.transitionDurationMs)}</dd>
        </dl>
        <p>
          Timer-running time records the accepted timer history. It does not
          describe what happened during that time.
        </p>
      </section>
      <p>
        {nextStep === null
          ? "No future first Step was selected."
          : `Next planned Step is ${closeout.items.find(({ stepId }) => stepId === nextStep.stepId)?.stepInstruction ?? "preserved in the future Daily Queue"} on ${formatLocalDate(nextStep.localDate)}, position ${nextStep.position}.`}
      </p>
      <p>
        {closeout.unresolvedHelpRequestIds.length}{" "}
        {closeout.unresolvedHelpRequestIds.length === 1
          ? "unresolved help request"
          : "unresolved help requests"}
        .
      </p>
    </section>
  );
}

type DecisionDraft = {
  kind: "" | QueueItemCloseoutDisposition["kind"];
  futureLocalDate: string;
  futurePosition: string;
};

type CloseDayOverride = ParentOverrideSnapshot & {
  scope: Extract<
    ParentOverrideSnapshot["scope"],
    { action: "close-day-with-unresolved-must-finish" }
  >;
};

function initialDecisionDrafts(preview: DailyQueueCloseoutPreview) {
  return Object.fromEntries(
    preview.items
      .filter(({ statusAtClose }) => statusAtClose.step !== "done")
      .map(({ queueItemId }) => [
        queueItemId,
        { kind: "", futureLocalDate: "", futurePosition: "1" },
      ]),
  ) as Record<string, DecisionDraft>;
}

function closeDayOverrideMatchesPreview(
  override: ParentOverrideSnapshot,
  preview: DailyQueueCloseoutPreview,
): override is CloseDayOverride {
  if (override.scope.action !== "close-day-with-unresolved-must-finish") {
    return false;
  }
  const scope = override.scope;
  if (
    scope.dailyQueueId !== preview.dailyQueueId ||
    scope.expectedQueueRevision !== preview.queueRevision ||
    scope.expectedSchoolworkRevision !== preview.schoolworkRevision ||
    scope.expectedWorkStateRevision !== preview.workStateRevision
  ) {
    return false;
  }
  return scope.reviewItems.every((reviewItem) => {
    const previewItem = preview.items.find(
      ({ queueItemId }) => queueItemId === reviewItem.queueItemId,
    );
    return (
      previewItem !== undefined &&
      previewItem.stepId === reviewItem.stepId &&
      previewItem.assignmentId === reviewItem.assignmentId &&
      previewItem.mustFinish === reviewItem.mustFinish &&
      previewItem.statusAtClose.step === reviewItem.statusAtClose.step &&
      previewItem.statusAtClose.assignment ===
        reviewItem.statusAtClose.assignment
    );
  });
}

function decisionMatchesReviewItem(
  decision: DecisionDraft | undefined,
  reviewItem: CloseDayOverride["scope"]["reviewItems"][number],
) {
  if (decision === undefined || decision.kind !== reviewItem.disposition.kind) {
    return false;
  }
  return reviewItem.disposition.kind !== "replan"
    ? true
    : decision.futureLocalDate === reviewItem.disposition.targetLocalDate &&
        Number(decision.futurePosition) ===
          reviewItem.disposition.carryoverPosition;
}

function decisionsFromOverride(
  preview: DailyQueueCloseoutPreview,
  override: CloseDayOverride,
) {
  const next = initialDecisionDrafts(preview);
  for (const item of override.scope.reviewItems) {
    next[item.queueItemId] =
      item.disposition.kind === "replan"
        ? {
            kind: "replan",
            futureLocalDate: item.disposition.targetLocalDate,
            futurePosition: String(item.disposition.carryoverPosition),
          }
        : {
            kind: item.disposition.kind,
            futureLocalDate: "",
            futurePosition: "1",
          };
  }
  return next;
}

export function DayCloseoutPanel({
  role,
  connectivity,
  preview,
  overrides: _overrides,
  onClose,
  onSafeAlternative,
  onRefresh,
}: {
  role: Role;
  connectivity: FocusBlockConnectivity;
  preview: DailyQueueCloseoutPreview;
  overrides: ParentOverrideStateSnapshot;
  onClose: (
    command: DayCloseoutUiCommand,
  ) => Promise<DailyQueueCloseoutCommandResult>;
  onSafeAlternative: (
    alternative: "continue" | "need-help" | "report-problem" | "safety-exit",
  ) => Promise<void | string>;
  onRefresh: () => Promise<void>;
}) {
  const [decisions, setDecisions] = useState(() =>
    initialDecisionDrafts(preview),
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [closeout, setCloseout] = useState<DailyQueueCloseoutSnapshot | null>(
    null,
  );
  const [localOverride, setLocalOverride] =
    useState<ParentOverrideSnapshot | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingSafeAction, setPendingSafeAction] = useState<string | null>(
    null,
  );
  const [safeActionStatus, setSafeActionStatus] = useState<string | null>(null);
  const [safeActionError, setSafeActionError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const closedRef = useRef<HTMLDivElement>(null);
  const overrideHeadingRef = useRef<HTMLHeadingElement>(null);
  const safeActionStatusRef = useRef<HTMLParagraphElement>(null);
  const safeActionErrorRef = useRef<HTMLParagraphElement>(null);

  const currentOverrides = _overrides.overrides
    .filter((override) => closeDayOverrideMatchesPreview(override, preview))
    .sort((left, right) => {
      const statePriority = { approved: 0, requested: 1 } as const;
      const leftPriority =
        left.state === "approved" || left.state === "requested"
          ? statePriority[left.state]
          : 2;
      const rightPriority =
        right.state === "approved" || right.state === "requested"
          ? statePriority[right.state]
          : 2;
      return (
        leftPriority - rightPriority ||
        right.requestedAt.localeCompare(left.requestedAt)
      );
    });
  const publishedOverride = currentOverrides.find((override) =>
    override.scope.reviewItems.every((item) =>
      decisionMatchesReviewItem(decisions[item.queueItemId], item),
    ),
  );
  const exactLocalOverride =
    localOverride !== null &&
    closeDayOverrideMatchesPreview(localOverride, preview) &&
    localOverride.scope.reviewItems.every((item) =>
      decisionMatchesReviewItem(decisions[item.queueItemId], item),
    )
      ? localOverride
      : null;
  const activeOverride = publishedOverride ?? exactLocalOverride;

  useEffect(() => {
    setDecisions((current) => {
      const next = initialDecisionDrafts(preview);
      for (const queueItemId of Object.keys(next)) {
        if (current[queueItemId] !== undefined) {
          next[queueItemId] = current[queueItemId]!;
        }
      }
      return next;
    });
    setLocalOverride(null);
    setError(null);
    setFieldErrors({});
  }, [
    preview.dailyQueueId,
    preview.queueRevision,
    preview.schoolworkRevision,
    preview.workStateRevision,
  ]);

  useEffect(() => {
    const discoverable = currentOverrides.find(
      ({ state }) => state === "requested" || state === "approved",
    );
    if (discoverable === undefined) return;
    setDecisions((current) => {
      if (Object.values(current).some(({ kind }) => kind !== "")) {
        return current;
      }
      return decisionsFromOverride(preview, discoverable);
    });
  }, [
    _overrides.revision,
    preview.dailyQueueId,
    preview.queueRevision,
    preview.schoolworkRevision,
    preview.workStateRevision,
  ]);

  useEffect(() => {
    if (error !== null) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    if (closeout === null) return;
    const heading = closedRef.current?.querySelector("h2");
    if (heading instanceof HTMLElement) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }, [closeout]);

  useEffect(() => {
    if (activeOverride !== undefined && activeOverride !== null) {
      overrideHeadingRef.current?.focus();
    }
  }, [activeOverride]);

  useEffect(() => {
    if (safeActionStatus !== null) safeActionStatusRef.current?.focus();
  }, [safeActionStatus]);

  useEffect(() => {
    if (safeActionError !== null) safeActionErrorRef.current?.focus();
  }, [safeActionError]);

  if (closeout !== null) {
    return (
      <div ref={closedRef}>
        <DayCloseoutHistory closeout={closeout} />
      </div>
    );
  }

  const updateDecision = (
    queueItemId: string,
    update: Partial<DecisionDraft>,
  ) => {
    setDecisions((current) => ({
      ...current,
      [queueItemId]: { ...current[queueItemId]!, ...update },
    }));
    setLocalOverride(null);
    setError(null);
    setFieldErrors({});
  };

  const runSafeAlternative = async (
    alternative: "continue" | "need-help" | "report-problem" | "safety-exit",
  ) => {
    setPendingSafeAction(alternative);
    setSafeActionStatus(null);
    setSafeActionError(null);
    try {
      const guidance = await onSafeAlternative(alternative);
      setSafeActionStatus(
        guidance ??
          {
            continue:
              "Continue remains available. Your closeout choices are unchanged.",
            "need-help":
              "Help was requested. Your closeout choices are unchanged.",
            "report-problem":
              "Problem options are available. Your closeout choices are unchanged.",
            "safety-exit":
              "The safety exit is available. Your closeout choices are unchanged.",
          }[alternative],
      );
    } catch (reason) {
      setSafeActionError(
        reason instanceof Error
          ? reason.message
          : "That safe action could not be confirmed. Your closeout choices are unchanged.",
      );
    } finally {
      setPendingSafeAction(null);
    }
  };

  const submit = async (overrideId?: string) => {
    const unfinished = preview.items.filter(
      ({ statusAtClose }) => statusAtClose.step !== "done",
    );
    if (unfinished.some(({ queueItemId }) => !decisions[queueItemId]?.kind)) {
      setError(
        "Choose what happens next for every unfinished Queue Item before closing the day.",
      );
      return;
    }
    const replanned = unfinished
      .map(({ queueItemId }) => decisions[queueItemId]!)
      .filter(({ kind }) => kind === "replan");
    if (
      replanned.some(
        ({ futureLocalDate, futurePosition }) =>
          futureLocalDate <= preview.localDate ||
          !Number.isInteger(Number(futurePosition)) ||
          Number(futurePosition) < 1,
      )
    ) {
      setError(
        "Replanned work needs a date after this Daily Queue and a positive whole-number position.",
      );
      return;
    }
    const futureTargets = replanned.map(
      ({ futureLocalDate, futurePosition }) =>
        `${futureLocalDate}:${Number(futurePosition)}`,
    );
    if (new Set(futureTargets).size !== futureTargets.length) {
      setError(
        "Each future Daily Queue position can be used only once on that date.",
      );
      return;
    }
    const dispositions: DayCloseoutUiCommand["dispositions"] = [];
    for (const item of unfinished) {
      const decision = decisions[item.queueItemId]!;
      if (decision.kind === "replan") {
        dispositions.push({
          queueItemId: item.queueItemId,
          disposition: {
            kind: decision.kind,
            futureLocalDate: decision.futureLocalDate,
            futurePosition: Number(decision.futurePosition),
          },
        });
      } else if (decision.kind !== "") {
        dispositions.push({
          queueItemId: item.queueItemId,
          disposition: { kind: decision.kind },
        });
      }
    }
    setPending(true);
    setError(null);
    setFieldErrors({});
    try {
      const result = await onClose({
        queueId: preview.dailyQueueId,
        expectedRevision: preview.queueRevision,
        expectedSchoolworkRevision: preview.schoolworkRevision,
        expectedWorkStateRevision: preview.workStateRevision,
        dispositions,
        ...(overrideId === undefined ? {} : { overrideId }),
      });
      if (result.status === "accepted") {
        setCloseout(result.snapshot);
        return;
      }
      if (result.status === "override-required") {
        setLocalOverride(result.override);
        return;
      }
      if (
        result.error.code !== "REVISION_CONFLICT" &&
        result.error.issues !== undefined
      ) {
        const mapped: Record<string, string> = {};
        for (const issue of result.error.issues) {
          const dispositionIndex = issue.path[1];
          const field = issue.path.at(-1);
          if (
            issue.path[0] === "dispositions" &&
            typeof dispositionIndex === "number" &&
            (field === "futureLocalDate" || field === "futurePosition")
          ) {
            const queueItemId = dispositions[dispositionIndex]?.queueItemId;
            if (queueItemId !== undefined) {
              mapped[`${queueItemId}:${field}`] = issue.message;
            }
          }
        }
        setFieldErrors(mapped);
      }
      setError(
        result.error.code === "REVISION_CONFLICT"
          ? `Another ${result.error.changedBy} session changed the Daily Queue. Your closeout choices remain here for review.`
          : result.error.message,
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The closeout was not confirmed. Your choices remain here.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="day-closeout-panel" aria-labelledby="closeout-title">
      <p className="status-label">End-of-day review</p>
      <h2 id="closeout-title">Close today without losing unfinished work</h2>
      <p>
        Review what is true now and choose one next place for every unfinished
        Queue Item.
      </p>
      <dl className="closeout-time-summary">
        <dt>Planned range</dt>
        <dd>
          {preview.timeSummary.plannedMinimumMinutes}–
          {preview.timeSummary.plannedMaximumMinutes} minutes
        </dd>
        <dt>Focus Block timer-running time</dt>
        <dd>
          {formatMinutes(preview.timeSummary.focusBlockTimerRunningDurationMs)}
        </dd>
        <dt>Break time</dt>
        <dd>{formatMinutes(preview.timeSummary.breakDurationMs)}</dd>
        <dt>Transition time</dt>
        <dd>{formatMinutes(preview.timeSummary.transitionDurationMs)}</dd>
      </dl>
      <ol className="closeout-item-list">
        {preview.items.map((item) => {
          const label = item.stepInstruction;
          const decision = decisions[item.queueItemId];
          return (
            <li key={item.queueItemId}>
              <h3>{label}</h3>
              <p>
                {item.courseTitle} · {item.assignmentTitle}
              </p>
              <p>
                <strong>{stepStatusLabels[item.statusAtClose.step]}</strong>
                {" · "}
                {assignmentStatusLabels[item.statusAtClose.assignment]}
                {item.mustFinish ? " · Must finish" : ""}
              </p>
              {item.statusAtClose.step === "done" ? (
                <p>Finished in this Daily Queue</p>
              ) : decision === undefined ? (
                <p role="status">
                  Choose a disposition after reviewing the refreshed Daily
                  Queue.
                </p>
              ) : (
                <>
                  <label htmlFor={`closeout-${item.queueItemId}`}>
                    End-of-day decision for {label}
                  </label>
                  <select
                    id={`closeout-${item.queueItemId}`}
                    value={decision.kind}
                    onChange={(event) =>
                      updateDecision(item.queueItemId, {
                        kind: event.target.value as DecisionDraft["kind"],
                      })
                    }
                  >
                    <option value="">Choose what happens next</option>
                    <option value="replan">Replan on a future date</option>
                    <option value="defer">Defer for later planning</option>
                    <option value="retain">Retain for parent review</option>
                    {role === "parent" ? (
                      <option value="parent-cancelled">Cancel as parent</option>
                    ) : null}
                  </select>
                  {decision.kind === "replan" ? (
                    <div className="closeout-replan-fields">
                      <label htmlFor={`closeout-date-${item.queueItemId}`}>
                        Future date for {label}
                      </label>
                      <input
                        id={`closeout-date-${item.queueItemId}`}
                        type="date"
                        min={nextLocalDate(preview.localDate)}
                        aria-describedby={
                          fieldErrors[`${item.queueItemId}:futureLocalDate`] ===
                          undefined
                            ? undefined
                            : `closeout-date-error-${item.queueItemId}`
                        }
                        aria-invalid={
                          fieldErrors[`${item.queueItemId}:futureLocalDate`] !==
                          undefined
                        }
                        value={decision.futureLocalDate}
                        onChange={(event) =>
                          updateDecision(item.queueItemId, {
                            futureLocalDate: event.target.value,
                          })
                        }
                      />
                      {fieldErrors[`${item.queueItemId}:futureLocalDate`] ===
                      undefined ? null : (
                        <p id={`closeout-date-error-${item.queueItemId}`}>
                          {fieldErrors[`${item.queueItemId}:futureLocalDate`]}
                        </p>
                      )}
                      <label htmlFor={`closeout-position-${item.queueItemId}`}>
                        Future position for {label}
                      </label>
                      <input
                        id={`closeout-position-${item.queueItemId}`}
                        type="number"
                        min={1}
                        step={1}
                        aria-describedby={
                          fieldErrors[`${item.queueItemId}:futurePosition`] ===
                          undefined
                            ? undefined
                            : `closeout-position-error-${item.queueItemId}`
                        }
                        aria-invalid={
                          fieldErrors[`${item.queueItemId}:futurePosition`] !==
                          undefined
                        }
                        value={decision.futurePosition}
                        onChange={(event) =>
                          updateDecision(item.queueItemId, {
                            futurePosition: event.target.value,
                          })
                        }
                      />
                      {fieldErrors[`${item.queueItemId}:futurePosition`] ===
                      undefined ? null : (
                        <p id={`closeout-position-error-${item.queueItemId}`}>
                          {fieldErrors[`${item.queueItemId}:futurePosition`]}
                        </p>
                      )}
                    </div>
                  ) : null}
                </>
              )}
            </li>
          );
        })}
      </ol>
      {activeOverride === undefined || activeOverride === null ? null : (
        <section
          className="closeout-override-state"
          aria-labelledby="closeout-override-title"
        >
          <p className="status-label">Protected must-finish work</p>
          <h3
            id="closeout-override-title"
            ref={overrideHeadingRef}
            tabIndex={-1}
          >
            Parent approval is needed to close this Daily Queue
          </h3>
          <p>
            {activeOverride.state === "requested"
              ? "Waiting for a parent decision. Your end-of-day choices remain unchanged."
              : activeOverride.state === "approved"
                ? "A parent approved this exact closeout. Use it once before it expires."
                : activeOverride.state === "denied"
                  ? "A parent denied this closeout. The Daily Queue and every unfinished item remain open."
                  : activeOverride.state === "expired"
                    ? "This approval window expired. Review the current Daily Queue before requesting again."
                    : "This Parent Override can no longer be used for another closeout."}
          </p>
          {activeOverride.state === "approved" ? (
            <button
              type="button"
              disabled={pending || connectivity !== "connected"}
              onClick={() => void submit(activeOverride.id)}
            >
              Use approval and close Daily Queue
            </button>
          ) : null}
          {role === "student" ? (
            <div
              className="closeout-safe-actions"
              aria-label="Actions that remain available"
            >
              {(
                [
                  ["continue", "Continue current Step"],
                  ["need-help", "Need Help"],
                  ["report-problem", "Report a problem"],
                  ["safety-exit", "Leave for safety"],
                ] as const
              ).map(([alternative, label]) => (
                <button
                  key={alternative}
                  type="button"
                  className="secondary-button"
                  disabled={pendingSafeAction === alternative}
                  onClick={() => void runSafeAlternative(alternative)}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
          {safeActionStatus === null ? null : (
            <p
              ref={safeActionStatusRef}
              role="status"
              aria-live="polite"
              tabIndex={-1}
            >
              {safeActionStatus}
            </p>
          )}
          {safeActionError === null ? null : (
            <p ref={safeActionErrorRef} role="alert" tabIndex={-1}>
              {safeActionError}
            </p>
          )}
        </section>
      )}
      {error === null ? null : (
        <div ref={errorRef} role="alert" tabIndex={-1}>
          <p>{error}</p>
          {error.includes("changed the Daily Queue") ? (
            <button
              type="button"
              className="secondary-button"
              disabled={refreshing || connectivity !== "connected"}
              onClick={() => {
                setRefreshing(true);
                void onRefresh().finally(() => setRefreshing(false));
              }}
            >
              {refreshing
                ? "Refreshing current Daily Queue…"
                : "Refresh and review current Daily Queue"}
            </button>
          ) : null}
        </div>
      )}
      <button
        type="button"
        disabled={pending || connectivity !== "connected"}
        onClick={() => void submit()}
      >
        {pending ? "Closing Daily Queue…" : "Review and close Daily Queue"}
      </button>
      {connectivity === "connected" ? null : (
        <p>
          Reconnect before closing. Every end-of-day choice remains on this
          screen.
        </p>
      )}
    </section>
  );
}
